import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@/lib/utils';
import {
  dialogBackdropVariants,
  dialogContentVariants,
} from '@/lib/motion';

type DialogProps = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

type DialogContextValue = {
  onOpenChange?: (open: boolean) => void;
};

const DialogContext = React.createContext<DialogContextValue | null>(null);

export const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  children,
}) => {
  React.useEffect(() => {
    if (!open || typeof document === 'undefined') {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange?.(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenChange, open]);

  return (
    <DialogContext.Provider value={{ onOpenChange }}>
      <AnimatePresence initial={false}>{open ? children : null}</AnimatePresence>
    </DialogContext.Provider>
  );
};

type DialogContentProps = {
  className?: string;
  children: React.ReactNode;
  closeOnBackdropClick?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      className = '',
      children,
      closeOnBackdropClick = true,
      onClick,
    },
    ref
  ) => {
    const dialog = React.useContext(DialogContext);

    const handleBackdropClick = () => {
      if (closeOnBackdropClick) {
        dialog?.onOpenChange?.(false);
      }
    };

    return (
      <>
        <motion.div
          key="dialog-backdrop"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]"
          variants={dialogBackdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
        <div className="fixed inset-0 z-[101] overflow-y-auto px-4 py-6 sm:px-6 sm:py-10">
          <div className="flex min-h-full items-center justify-center">
            <motion.div
              ref={ref}
              key="dialog-content"
              className={cn(
                'grid w-full max-w-[calc(100%-2rem)] gap-3 rounded-xl border border-border/40 bg-popover p-5 text-popover-foreground shadow-2xl sm:max-w-lg',
                className
              )}
              variants={dialogContentVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onClick={(event) => {
                event.stopPropagation();
                onClick?.(event);
              }}
              role="dialog"
              aria-modal="true"
            >
              {children}
            </motion.div>
          </div>
        </div>
      </>
    );
  }
);
DialogContent.displayName = 'DialogContent';

type DialogHeaderProps = {
  className?: string;
  children: React.ReactNode;
};

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  className = '',
  children,
}) => {
  return <div className={cn('flex flex-col gap-1.5', className)}>{children}</div>;
};

type DialogTitleProps = {
  className?: string;
  children: React.ReactNode;
};

export const DialogTitle: React.FC<DialogTitleProps> = ({
  className = '',
  children,
}) => {
  return <h2 className={cn('text-sm font-semibold', className)}>{children}</h2>;
};

export default Dialog;
